import React, { FC, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import { dashboardPagesMenu } from '../../../menu';
import { startTransition } from 'react';

type TView = 'login' | 'forgotRequest' | 'forgotReset';
type TAlertColor = 'danger' | 'success' | 'info';

interface ILoginHeaderProps {
	isNewUser?: boolean;
	view?: TView;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser, view }) => {
	if (view === 'forgotRequest' || view === 'forgotReset') {
		return (
			<>
				<div className='text-center h1 fw-bold mt-4'>Forgot Password</div>
				<div className='text-center h4 text-muted mb-5'>
					{view === 'forgotRequest'
						? 'Enter your registered email to receive an OTP'
						: 'Enter the OTP and your new password'}
				</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-4'>Welcome,</div>
			<div className='text-center h4 text-muted mb-5'>Sign in to continue!</div>
		</>
	);
};

LoginHeader.defaultProps = {
	isNewUser: false,
	view: 'login',
};

const MIN_PASSWORD_LENGTH = 6;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ILoginProps {
	isSignUp?: boolean;
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { setUser } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();
	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [showAlert, setShowAlert] = useState<boolean>(false);
	const [errMsg, setErrMsg] = useState<string>('');
	const [alertColor, setAlertColor] = useState<TAlertColor>('danger');
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Forgot / reset password state
	const [view, setView] = useState<TView>('login');
	const [fpEmail, setFpEmail] = useState<string>('');
	const [fpOtp, setFpOtp] = useState<string>('');
	const [fpNewPassword, setFpNewPassword] = useState<string>('');
	const [fpConfirmPassword, setFpConfirmPassword] = useState<string>('');
	const [fpLoading, setFpLoading] = useState<boolean>(false);

	const navigate = useNavigate();
	// const handleOnClick = useCallback(() => , [navigate],);

	const showMessage = (message: string, color: TAlertColor) => {
		setErrMsg(message);
		setAlertColor(color);
		setShowAlert(true);
	};

	const hideMessage = () => {
		setShowAlert(false);
		setErrMsg('');
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginUsername?: string; loginPassword?: string } = {};
			if (!values.loginUsername) {
				errors.loginUsername = 'Please enter username';
			}
			if (!values.loginPassword) {
				errors.loginPassword = 'Please enter password';
			}
			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			setIsLoading(true);
			// startTransition(() => {
			// navigate(`../${dashboardPagesMenu.dashboard.path}`);
			// });
			try {
				await fetch(process.env.REACT_APP_NODEJS_URL + 'users/login_users', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						username: formik.values.loginUsername,
						password: formik.values.loginPassword,
					}),
				}).then(async (response) => {
					const result = await response.json();
					if (result.status) {
						if (setUser) {
							setUser(result.data.first_name + ' ' + result.data.last_name);
							localStorage.setItem(
								'authUsername',
								result.data.first_name + ' ' + result.data.last_name,
							);
						}
						localStorage.setItem('userData', JSON.stringify(result.data));
						navigate("/");
						// navigate(`../${dashboardPagesMenu.dashboard.path}`);
					} else {
						setIsLoading(false);
						showMessage(result.message, 'danger');
					}
				});
			} catch (error) {
				setIsLoading(false);
				console.error('Error:', error);
			}
		},
	});

	/* ---------------- Forgot / reset password ---------------- */

	const postJson = async (path: string, body: object) => {
		const response = await fetch(process.env.REACT_APP_NODEJS_URL + path, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
		return response.json();
	};

	const openForgotPassword = () => {
		hideMessage();
		setFpEmail('');
		setFpOtp('');
		setFpNewPassword('');
		setFpConfirmPassword('');
		setView('forgotRequest');
	};

	const backToLogin = () => {
		hideMessage();
		setFpOtp('');
		setFpNewPassword('');
		setFpConfirmPassword('');
		setView('login');
	};

	const handleSendOtp = async () => {
		if (!EMAIL_REGEX.test(fpEmail.trim())) {
			showMessage('Please enter a valid email address.', 'danger');
			return;
		}
		setFpLoading(true);
		try {
			const result = await postJson('users/forgot_password', {
				email: fpEmail.trim(),
			});
			showMessage(result.message, result.status ? 'success' : 'danger');
			if (result.status) {
				setFpOtp('');
				setView('forgotReset');
			}
		} catch (error) {
			console.error('Error:', error);
			showMessage('Something went wrong. Please try again.', 'danger');
		} finally {
			setFpLoading(false);
		}
	};

	const handleResetPassword = async () => {
		if (!/^\d{6}$/.test(fpOtp)) {
			showMessage('Please enter the 6-digit OTP.', 'danger');
			return;
		}
		if (fpNewPassword.length < MIN_PASSWORD_LENGTH) {
			showMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`, 'danger');
			return;
		}
		if (fpNewPassword !== fpConfirmPassword) {
			showMessage('Passwords do not match.', 'danger');
			return;
		}
		setFpLoading(true);
		try {
			const result = await postJson('users/reset_password', {
				email: fpEmail.trim(),
				otp: fpOtp,
				new_password: fpNewPassword,
			});
			if (result.status) {
				formik.resetForm();
				setFpEmail('');
				setFpOtp('');
				setFpNewPassword('');
				setFpConfirmPassword('');
				setView('login');
				showMessage(result.message, 'success');
			} else {
				showMessage(result.message, 'danger');
			}
		} catch (error) {
			console.error('Error:', error);
			showMessage('Something went wrong. Please try again.', 'danger');
		} finally {
			setFpLoading(false);
		}
	};

	return (
		<PageWrapper
			isProtected={false}
			title={'Login'}
			className={classNames({ 'bg-dark': true })}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-3'>
									<Logo width={200} />
								</div>
								<div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-dark': darkModeStatus,
									})}></div>
								<LoginHeader isNewUser={true} view={view} />
								{showAlert ? (
									<Alert
										isLight
										color={alertColor}
										icon={alertColor === 'success' ? 'CheckCircle' : 'Lock'}>
										<div className='row'>
											<div className='col-12'>{errMsg}</div>
										</div>
									</Alert>
								) : null}

								{/* ---------------- Login ---------------- */}
								{view === 'login' && (
									<>
										<form className='row g-4'>
											<div className='col-12'>
												<FormGroup
													id='loginUsername'
													isFloating
													label='Username'
													className={classNames({
														'd-none': signInPassword,
													})}>
													<Input
														autoComplete='username'
														value={formik.values.loginUsername}
														isTouched={formik.touched.loginUsername}
														invalidFeedback={formik.errors.loginUsername}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>
												<div className='container' style={{ height: '20px' }}></div>
												<FormGroup
													id='loginPassword'
													isFloating
													label='Password'
													className={classNames({
														'd-none': signInPassword,
													})}>
													<Input
														type='password'
														autoComplete='current-password'
														value={formik.values.loginPassword}
														isTouched={formik.touched.loginPassword}
														invalidFeedback={formik.errors.loginPassword}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													icon='login'
													color='warning'
													className='w-100 py-3'
													onClick={formik.handleSubmit}>
													{isLoading && <Spinner isSmall inButton isGrow />}
													Login
												</Button>
											</div>
											<div className='container' style={{ height: '20px' }}></div>
										</form>
										<div className='text-end mb-1 mt-0'>
											<Button color='info' isLink onClick={openForgotPassword}>
												Forgot Password ?
											</Button>
										</div>
									</>
								)}

								{/* ---------------- Step 1: request OTP ---------------- */}
								{view === 'forgotRequest' && (
									<form className='row g-4'>
										<div className='col-12'>
											<FormGroup id='fpEmail' isFloating label='Email'>
												<Input
													type='email'
													autoComplete='email'
													value={fpEmail}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														setFpEmail(e.target.value)
													}
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<Button
												icon='Send'
												color='warning'
												className='w-100 py-3'
												isDisable={fpLoading}
												onClick={handleSendOtp}>
												{fpLoading && <Spinner isSmall inButton isGrow />}
												Send OTP
											</Button>
										</div>
										<div className='col-12 text-center'>
											<Button color='info' isLink onClick={backToLogin}>
												Back to Login
											</Button>
										</div>
									</form>
								)}

								{/* ---------------- Step 2: verify OTP + new password ---------------- */}
								{view === 'forgotReset' && (
									<form className='row g-4'>
										<div className='col-12'>
											<FormGroup id='fpOtp' isFloating label='OTP'>
												<Input
													autoComplete='one-time-code'
													value={fpOtp}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
													}
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<FormGroup id='fpNewPassword' isFloating label='New Password'>
												<Input
													type='password'
													autoComplete='new-password'
													value={fpNewPassword}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														setFpNewPassword(e.target.value)
													}
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<FormGroup
												id='fpConfirmPassword'
												isFloating
												label='Confirm New Password'>
												<Input
													type='password'
													autoComplete='new-password'
													value={fpConfirmPassword}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
														setFpConfirmPassword(e.target.value)
													}
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											<Button
												icon='LockReset'
												color='warning'
												className='w-100 py-3'
												isDisable={fpLoading}
												onClick={handleResetPassword}>
												{fpLoading && <Spinner isSmall inButton isGrow />}
												Reset Password
											</Button>
										</div>
										<div className='col-12 d-flex justify-content-between'>
											<Button
												color='info'
												isLink
												isDisable={fpLoading}
												onClick={handleSendOtp}>
												Resend OTP
											</Button>
											<Button color='info' isLink onClick={backToLogin}>
												Back to Login
											</Button>
										</div>
									</form>
								)}
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};
Login.propTypes = {
	isSignUp: PropTypes.bool,
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;